import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { Icon, MenuItem, Sidebar, SidebarSection, toast } from '@node-space/ui-components'
import { appBannersId } from 'components/app/AppBanners'
import { DurationInMilliseconds, PathNames } from 'constants/General'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useSwitchAccountMutation } from 'hooks/mutations/useSwitchAccountMutation'
import { SidebarLinkItem, useSidebarLinks } from 'hooks/sidebar/useSidebarLinks'
import { useUserMenuItems } from 'hooks/sidebar/useUserMenuItems'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { reloadPage } from 'utils/utils'

const SideBar = () => {
  const location = useLocation()
  const { t } = useTranslation()
  const { links, footerLink } = useSidebarLinks()
  const userMenuItems = useUserMenuItems()
  const { profile } = useProfileContext()
  const { accounts, currentAccount } = useAccountsContext()
  const { mutate: switchAccount } = useSwitchAccountMutation()
  const navigate = useNavigate()
  const bannerSpacing = useComponentHeight(appBannersId)

  const currentPath = location.pathname

  const getSharedMenuItemFields = (sidebarLink: SidebarLinkItem): MenuItem => {
    const { title, to, icon, highlight } = sidebarLink
    const toPath = to?.toString() ?? ''
    const isActive = currentPath === to || currentPath.startsWith(`${toPath}/`)
    return {
      title,
      Icon: icon,
      isActive,
      badgeCount: highlight ? Number(highlight) : undefined,
      disabled: sidebarLink?.requiresVerification,
      onClick: () => navigate(toPath),
    }
  }

  const sidebarSections: SidebarSection[] = useMemo(
    () => [
      {
        title: 'Navigation',
        items: links
          .filter(({ isHidden }) => !isHidden)
          .map<MenuItem>(sidebarLink => {
            const sharedMainFields = getSharedMenuItemFields(sidebarLink)
            return {
              ...sharedMainFields,
              sub: sidebarLink?.subItems
                ?.filter(({ isHidden }) => !isHidden)
                ?.map(getSharedMenuItemFields),
            }
          }),
      },
      {
        items: [
          {
            to: `${PathNames.SETTINGS}${PathNames.MANAGE_ACCOUNT}`,
            title: t('manageAccount'),
            Icon: <Icon name="Gear" />,
            onClick: () => navigate(`${PathNames.SETTINGS}${PathNames.MANAGE_ACCOUNT}`),
          },
        ],
      },
    ],
    [links, location.pathname]
  )

  const accountSwitcherProps = useMemo(
    () => ({
      workspaceAccounts: {
        accountName: profile ? `${profile.firstName} ${profile.lastName}` : '',
        accounts:
          accounts?.map(account => ({
            ...account,
            id: account.id.toString(),
          })) ?? [],
        selectedAccount: currentAccount
          ? {
              ...currentAccount,
              id: currentAccount.id.toString(),
            }
          : undefined,
        setSelectedAccount: (index: number) => {
          const accountIdToSwitchTo = accounts?.[index]?.id
          if (accounts?.length && currentAccount?.id !== accountIdToSwitchTo) {
            switchAccount(accountIdToSwitchTo, {
              onSuccess: () => {
                if (
                  location.pathname.includes(PathNames.CUSTOMERS) ||
                  location.pathname.includes(PathNames.WALLETS)
                ) {
                  navigate(PathNames.DASHBOARD)
                }
                reloadPage()
              },
              onError: () => {
                toast({
                  header: `${t('switchAccountError')} - ${accounts?.[index]?.name}`,
                  variant: 'error',
                  duration: DurationInMilliseconds.TWENTY_SECONDS,
                })
              },
            })
          }
        },
      },
      menuListItems: userMenuItems,
      filter: accounts?.length > 6,
    }),
    [profile, accounts, currentAccount, switchAccount, userMenuItems, location.pathname]
  )

  return (
    <Sidebar
      topOffset={bannerSpacing}
      sidebarSections={sidebarSections}
      accountSwitcher={accountSwitcherProps}
      footer={{
        title: footerLink?.title,
        logoUrl: footerLink?.logoUrl,
        url: footerLink?.url,
        onClick: () => {
          if (footerLink?.to) {
            navigate(footerLink?.to)
          }
        },
      }}
    />
  )
}

export default SideBar
